import { clipKey } from '../core/footage';
import type { ClipScan } from '../core/scan';
import type { LibraryClip } from './library';

export const PRIORITY_VIEWER = 2;
export const PRIORITY_TRIAGE = 1;

export interface ScanRequest {
  readonly clip: LibraryClip;
  readonly telemetry: boolean;
}

export type ScanRunner = (request: ScanRequest) => Promise<ClipScan>;
export type SettledListener = (request: ScanRequest, result: ClipScan | Error) => void;

interface Pending {
  readonly request: ScanRequest;
  readonly priority: number;
  readonly order: number;
}

export class ScanQueue {
  private readonly pending = new Map<string, Pending>();
  private readonly started = new Set<string>();
  private running = 0;
  private order = 0;
  private drained: (() => void)[] = [];

  constructor(
    private readonly run: ScanRunner,
    private readonly concurrency: number,
    private readonly onSettled: SettledListener,
  ) {}

  add(clips: readonly LibraryClip[], priority: number): void {
    for (const clip of clips) {
      const key = clipKey(clip);
      if (this.started.has(key)) continue;
      const queued = this.pending.get(key);
      if (queued && queued.priority >= priority) continue;
      this.pending.set(key, { request: { clip, telemetry: clip.camera === 'front' }, priority, order: queued?.order ?? this.order++ });
    }
    this.pump();
  }

  idle(): Promise<void> {
    if (this.running === 0 && this.pending.size === 0) return Promise.resolve();
    return new Promise((resolve) => this.drained.push(resolve));
  }

  private next(): [string, Pending] | undefined {
    let best: [string, Pending] | undefined;
    for (const entry of this.pending) {
      if (!best || entry[1].priority > best[1].priority || (entry[1].priority === best[1].priority && entry[1].order < best[1].order)) best = entry;
    }
    return best;
  }

  private pump(): void {
    for (let next = this.next(); next && this.running < this.concurrency; next = this.next()) {
      const [key, { request }] = next;
      this.pending.delete(key);
      this.started.add(key);
      this.running++;
      this.run(request)
        .then((scan) => this.settle(request, scan), (error: unknown) => this.settle(request, error instanceof Error ? error : new Error(String(error))));
    }
  }

  private settle(request: ScanRequest, result: ClipScan | Error): void {
    this.running--;
    this.onSettled(request, result);
    this.pump();
    if (this.running === 0 && this.pending.size === 0) this.drained.splice(0).forEach((resolve) => resolve());
  }
}
